import { JSX, useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';

import usePasswordField from './usePasswordField';
import VisibilityToggle from './VisibilityToggle';
import { AccountFieldsetField as Field } from '../../AccountFieldset.style';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../constants';
import ValidationBox from '../ValidationBox';

/**
 * Password field component that handles form integration and UI rendering.
 *
 * @param {object} props - Component props
 * @param {string} [props.defaultValue] - Default value for the field
 * @param {string} [props.namePrefix] - Prefix for the field name
 * @param {boolean} [props.readOnly] - Whether the field is read-only
 * @param {object} [props.validation] - Additional validation rules
 * @param {string} [props.value] - Value for the field
 * @returns {JSX.Element} Component
 */
function PasswordField({
  defaultValue,
  namePrefix,
  readOnly,
  validation,
  value: valueProp,
  ...delegated
}) {
  const { formatMessage } = useIntl();
  const { error, field } = usePasswordField({ defaultValue, namePrefix, validation });
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const isDisabled = readOnly || delegated.disabled;

  const autoFocus = !isDisabled && delegated.autoFocus;
  const errorProp = !readOnly ? error : null;
  const fieldProps = !readOnly ? field : {};
  const type = isVisible ? 'text' : 'password';
  const value = readOnly ? valueProp : field.value;

  const icon = <VisibilityToggle isVisible={isVisible} onToggle={toggleVisibility} />;

  const inputProps = {
    'data-testid': 'password-input',
    autoComplete: 'new-password',
    maxLength: PASSWORD_MAX_LENGTH,
    minLength: PASSWORD_MIN_LENGTH
  };

  const MESSAGE_IDS = {
    label: 'shopping.component.accountFieldset.passwordField.label'
  };

  return (
    <>
      <Field
        autoFocus={autoFocus}
        label={formatMessage({ id: MESSAGE_IDS.label })}
        type={type}
        {...delegated}
        {...fieldProps}
        error={errorProp}
        icon={icon}
        iconPosition="end"
        InputProps={{ inputProps }}
        readOnly={readOnly}
        value={value}
      />
      {!isDisabled && <ValidationBox error={error} value={value} />}
    </>
  );
}

PasswordField.propTypes = {
  defaultValue: PropTypes.string,
  namePrefix: PropTypes.string,
  readOnly: PropTypes.bool,
  validation: PropTypes.object,
  value: PropTypes.string
};

export default PasswordField;
